import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import Vue from 'vue';
import {
    getSwiper,
    getSwiperModules,
    handleClickOnDisclaimerLink,
} from '../utils';

Vue.component('homepage-thumb-slider', {
    props: {
        totalSlides: {
            type: [Number, String],
            default: null,
        },
        siteHandle: {
            type: String,
            default: '',
        },
        disablePagination: {
            type: [Boolean, String],
            default: false,
        },
        customSliderClass: {
            type: String,
            default: '',
        },
        parentClass: {
            type: String,
            default: '',
        },
        switchSlidesAutomatically: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            currentSlide: 0,
            gallery: null,
            slides: null,
        };
    },

    computed: {},

    mounted() {
        window.addEventListener('click', handleClickOnDisclaimerLink);
        this.initSwiper();
    },

    methods: {
        async initSwiper() {
            if (this.gallery) {
                // Update the existing swiper instance
                this.gallery.updateSlides();
                this.gallery.updateSize();
                this.gallery.updateProgress();
                this.gallery.updateSlidesClasses();

                // Force scrollbar update
                this.gallery.scrollbar.updateSize();
                this.gallery.scrollbar.init();
                this.gallery.scrollbar.setTranslate();

                // Force navigation update
                if (this.gallery.navigation) {
                    this.gallery.navigation.update();
                }
                this.gallery.update();
            } else {
                const { Swiper } = await getSwiper();
                const {
                    Autoplay,
                    Scrollbar,
                    Navigation,
                    Pagination,
                    A11y,
                    Keyboard,
                } = await getSwiperModules();

                const slider = this.$el.querySelector(
                    this.customSliderClass
                        ? `.${this.customSliderClass}`
                        : '.thumbslider__content',
                );

                const parent = this.parentClass
                    ? (this.$el.closest(`.${this.parentClass}`) ?? this.$el)
                    : this.$el;
                const scrollbar = parent.querySelector('.swiper-scrollbar');
                const prevButton = parent.querySelector('.swiper-button-prev');
                const nextButton = parent.querySelector('.swiper-button-next');
                const shouldAutoSwipe = this.switchSlidesAutomatically;

                this.gallery = new Swiper(
                    this.customSliderClass
                        ? `.${this.customSliderClass}`
                        : '.thumbslider__content',
                    {
                        modules: [
                            Autoplay,
                            Scrollbar,
                            Navigation,
                            Pagination,
                            A11y,
                            Keyboard,
                        ],
                        keyboard: {
                            enabled: true,
                        },
                        slidesOffsetAfter: 20,
                        slidesOffsetBefore: 20,
                        slideActiveClass: 'swiper-slide-active',
                        watchSlidesProgress: true,
                        loop: false,
                        noSwiping: false,
                        centeredSlides: false,
                        slidesPerGroup: 1,
                        slidesPerView: 1.1,
                        spaceBetween: 16,
                        breakpoints: {
                            768: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 30,
                                slidesOffsetBefore: 30,
                            },
                            1024: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 30,
                                slidesOffsetBefore: 30,
                            },
                            1200: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 30,
                                slidesOffsetBefore: 30,
                            },
                            1280: {
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            1400: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            1600: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            1800: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            2000: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            2200: {
                                slidesPerView: 1.1,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            2400: {
                                slidesPerView: 1.2,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            2600: {
                                slidesPerView: 1.4,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            2800: {
                                slidesPerView: 1.8,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                            3000: {
                                slidesPerView: 2.2,
                                spaceBetween: 32,
                                slidesOffsetAfter: 40,
                                slidesOffsetBefore: 40,
                            },
                        },
                        scrollbar: {
                            el: scrollbar,
                            hide: false,
                            draggable: true,
                        },
                        uniqueNavElements: true,
                        navigation: {
                            nextEl: nextButton,
                            prevEl: prevButton,
                        },
                        pagination: {
                            enabled: !Boolean(this.disablePagination),
                            el: slider.querySelector('.swiper-pagination'),
                            clickable: true,
                            renderBullet: (index, className) =>
                                `<span class="${className}"><span></span></span>`,
                        },
                        ...(shouldAutoSwipe && {
                            autoplay: {
                                delay: 7000,
                                disableOnInteraction: true,
                            },
                        }),
                        speed: 450,
                    },
                )
                    .on('slideChange', (swiper) => {
                        this.currentSlide = swiper.activeIndex;

                        const isLastSlide =
                            swiper.activeIndex === swiper.slides.length - 1;

                        const shouldAutoplayReset =
                            shouldAutoSwipe &&
                            isLastSlide &&
                            swiper.autoplay?.running;

                        if (shouldAutoplayReset) {
                            swiper.autoplay.stop();
                            setTimeout(() => {
                                swiper.slideTo(0);
                                swiper.autoplay.start();
                            }, swiper.params.autoplay.delay);
                        }
                    })
                    .on('sliderMove', () => {
                        this.addFullyVisibleClass();
                    })
                    .on('transitionEnd', () => {
                        this.addFullyVisibleClass();
                    })
                    .on('transitionStart', () => {
                        this.addFullyVisibleClass();
                    })
                    .on('resize', () => {
                        this.addFullyVisibleClass();
                    });

                Vue.nextTick(() => {
                    if (!this.gallery || !this.gallery.scrollbar) return;

                    this.gallery.scrollbar.updateSize();
                    this.gallery.scrollbar.setTranslate();
                });

                this.addFullyVisibleClass();
            }
        },

        slideTo(slide) {
            this.gallery.slideTo(slide);
        },

        addFullyVisibleClass() {
            const slides =
                this.slides ?? this.$el.querySelectorAll('.swiper-slide');

            // check if each slide is fully visible
            slides.forEach((slide) => {
                const slideRect = slide.getBoundingClientRect();
                const containerRect = this.$el.getBoundingClientRect();

                if (
                    slideRect.left >= containerRect.left &&
                    slideRect.right <= containerRect.right
                ) {
                    slide.classList.add('swiper-slide-fully-visible');
                } else {
                    slide.classList.remove('swiper-slide-fully-visible');
                }
            });
        },
    },
});
